/**
 * Address formatting utilities
 * Provides consistent address formatting across the application
 */

import { RACQAddressResult } from '@/interfaces';

/**
 * Formats an address with consistent formatting rules:
 * - Street line parts joined with space, followed by comma
 * - No comma after suburb
 * - Format: "123 Main Street, Brisbane QLD 4000"
 * - Overseas format: "123 Main Street, Brisbane 4000, Country"
 * 
 * @param address - The address object to format
 * @param includeCountry - Whether to include country (for overseas addresses)
 * @returns Formatted address string, or empty string if no address provided
 */
export const formatAddress = (address?: RACQAddressResult, includeCountry = false): string => {
  if (!address) {
    return '';
  }

  const parts = buildAddressLineParts(address);
  const locationParts = buildLocationParts(address, includeCountry);
  
  return [...parts, ...locationParts].join(' ').trim() || '';
};

/**
 * Builds the address line parts (unit, street number, street name, etc.)
 * Adds comma after the last part
 */
function buildAddressLineParts(address: RACQAddressResult): string[] {
  const parts = [];

  // If addressLine is already populated, use it directly
  if (address?.addressLine) {
    parts.push(address.addressLine);

    // Add a comma to the AddressLine if needed.
    if (!address.addressLine.toLowerCase().includes('po box') || !address.addressLine.includes(',')) {
      const lastIndex = parts.length - 1;
      parts[lastIndex] = parts[lastIndex] + ',';
    }
    return parts;
  }

  // Regular address construction from individual fields
  if (address.unitNumber) parts.push(address.unitNumber);
  if (address.buildingLevel) parts.push(address.buildingLevel);
  if (address.buildingName) parts.push(address.buildingName);
  if (address.streetNumber) parts.push(address.streetNumber);
  if (address.street) parts.push(address.street);

  // Add comma after street line if there are any parts
  if (parts.length > 0) {
    const lastIndex = parts.length - 1;
    parts[lastIndex] = parts[lastIndex] + ',';
  }

  return parts;
}

/**
 * Builds the location parts (suburb, state, postcode, country)
 * No commas between suburb/state/postcode
 * Comma before country if included
 */
function buildLocationParts(address: RACQAddressResult, includeCountry = false): string[] {
  const parts = [];
  
  if (address.suburb) parts.push(address.suburb);
  if (address.state) parts.push(address.state);
  if (address.postcode) parts.push(address.postcode);
  
  // Add country with comma separator for overseas addresses
  if (includeCountry && address.country) {
    if (parts.length > 0) {
      const lastIndex = parts.length - 1;
      parts[lastIndex] = parts[lastIndex] + ',';
    }
    parts.push(address.country);
  }
  
  return parts;
}
